import React, { useState, useEffect } from 'react';
import {
  Users, Search, MapPin, Mail, Phone, Filter, X,
  Calendar, BarChart3, UserPlus, Edit2, Trash2, AlertTriangle,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import CustomerFormModal from '../shared/CustomerFormModal';
import PageIdentifier from '../shared/PageIdentifier';
import usePermissions from '../../hooks/usePermissions';

const CustomerList = () => {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('customer-list', 'delete');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterState, setFilterState] = useState('');
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [showCityStats, setShowCityStats] = useState(false);
  const [cityStatistics, setCityStatistics] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteConfirmCustomer, setDeleteConfirmCustomer] = useState(null); // for panel delete
  const [deleteBlockInfo, setDeleteBlockInfo] = useState(null);             // scheme block details
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    fetchCustomers();
    fetchCityStats();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCity, filterState, sortBy]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/customers?page=1&limit=5000');
      const data = await response.json();

      if (data.success) {
        setCustomers(data.data);

        // Extract unique cities and states
        const uniqueCities = [...new Set(data.data.map(c => c.city).filter(Boolean))];
        const uniqueStates = [...new Set(data.data.map(c => c.state).filter(Boolean))];
        setCities(uniqueCities.sort());
        setStates(uniqueStates.sort());
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      alert('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCityStats = async () => {
    try {
      const response = await fetch('/api/customers/city-statistics');
      const data = await response.json();
      if (data.success) {
        setCityStatistics(data.data);
      }
    } catch (error) {
      console.error('Error fetching city statistics:', error);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await fetch(`/api/customers/${customerId}`);
      const data = await response.json();
      if (data.success) {
        setSelectedCustomer(data.data);
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterCity('');
    setFilterState('');
    setSortBy('newest');
  };

  const handleCustomerCreated = (newCustomer) => {
    setShowCustomerModal(false);
    setEditingCustomer(null);
    fetchCustomers();
    // If we were editing the currently selected customer, refresh it
    if (selectedCustomer && newCustomer?.id === selectedCustomer.id) {
      setSelectedCustomer(newCustomer);
    }
  };

  const handleEditCustomer = (customer, e) => {
    e?.stopPropagation();
    setEditingCustomer(customer);
    setShowCustomerModal(true);
  };

  const handleCustomerDeleted = (customerId) => {
    setCustomers(prev => prev.filter(c => c.id !== customerId));
    if (selectedCustomer?.id === customerId) setSelectedCustomer(null);
    setShowCustomerModal(false);
    setEditingCustomer(null);
  };

  const handleDeleteFromPanel = async (customer) => {
    if (!canDelete) return;
    try {
      const res = await fetch(`/api/customers/${customer.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!data.success) {
        if (data.code === 'SCHEME_ENROLLMENT_ACTIVE') {
          setDeleteConfirmCustomer(null);
          setDeleteBlockInfo({ customer, enrollments: data.enrollments, message: data.error });
          return;
        }
        throw new Error(data.error || 'Delete failed');
      }
      handleCustomerDeleted(customer.id);
    } catch (err) {
      alert(`Failed to delete customer: ${err.message}`);
    } finally {
      setDeleteConfirmCustomer(null);
    }
  };

  // Filter and sort customers
  const filteredCustomers = customers
    .filter(customer => {
      const matchesSearch = !searchTerm ||
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.includes(searchTerm) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.customer_code?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCity = !filterCity || customer.city === filterCity;
      const matchesState = !filterState || customer.state === filterState;

      return matchesSearch && matchesCity && matchesState;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':  return (a.name || '').localeCompare(b.name || '');
        case 'name_desc': return (b.name || '').localeCompare(a.name || '');
        case 'newest':    return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':    return new Date(a.created_at) - new Date(b.created_at);
        case 'city':      return (a.city || '').localeCompare(b.city || '');
        default:          return 0;
      }
    });

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / PAGE_SIZE));
  const pagedCustomers = filteredCustomers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <PageIdentifier screenId="CUST-001" screenName="Customer List" />

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="text-blue-600" size={32} />
              Customer Management
            </h1>
            <p className="text-gray-600 mt-1">
              View and manage all customers with city-based categorization
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCustomerModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <UserPlus size={20} />
              Create Customer
            </button>
            <button
              onClick={() => setShowCityStats(!showCityStats)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <BarChart3 size={20} />
              {showCityStats ? 'Hide' : 'Show'} City Analytics
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
              </div>
              <Users className="text-blue-600" size={32} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cities</p>
                <p className="text-2xl font-bold text-gray-900">{cities.length}</p>
              </div>
              <MapPin className="text-green-600" size={32} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">States</p>
                <p className="text-2xl font-bold text-gray-900">{states.length}</p>
              </div>
              <MapPin className="text-purple-600" size={32} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Filtered Results</p>
                <p className="text-2xl font-bold text-gray-900">{filteredCustomers.length}</p>
              </div>
              <Filter className="text-amber-600" size={32} />
            </div>
          </div>
        </div>

        {/* City Statistics Panel */}
        {showCityStats && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="text-blue-600" size={24} />
              City-wise Statistics
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Customers</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Estimates</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">INR Revenue</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">USD Revenue</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {cityStatistics.map((stat, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {stat.city}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {stat.state}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {stat.total_customers}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {stat.total_estimates}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        ₹{parseFloat(stat.total_inr_revenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        ${parseFloat(stat.total_usd_revenue || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, phone, email, or customer code..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* City Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by City
              </label>
              <select
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Cities</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {/* State Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by State
              </label>
              <select
                value={filterState}
                onChange={(e) => setFilterState(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All States</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="name_asc">Name (A–Z)</option>
                <option value="name_desc">Name (Z–A)</option>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="city">City (A–Z)</option>
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          {(searchTerm || filterCity || filterState || sortBy !== 'newest') && (
            <div className="mt-4">
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X size={16} />
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Customer List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <Users className="mx-auto mb-4 text-gray-400" size={48} />
                    <p className="text-lg">No customers found</p>
                    <p className="text-sm">Try adjusting your search or filters</p>
                  </td>
                </tr>
              ) : (
                pagedCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => fetchCustomerDetails(customer.id)}
                        className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                      >
                        {customer.customer_code}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center gap-1">
                        <Phone size={14} className="text-gray-400" />
                        {customer.phone || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center gap-1">
                        <Mail size={14} className="text-gray-400" />
                        {customer.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center gap-1">
                        <MapPin size={14} className="text-gray-400" />
                        {customer.city || 'N/A'}
                      </div>
                      {customer.state && (
                        <div className="text-sm text-gray-500">{customer.state}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} className="text-gray-400" />
                        {new Date(customer.created_at).toLocaleDateString('en-IN')}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredCustomers.length > 0 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredCustomers.length)} of {filteredCustomers.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Scheme block dialog — cannot delete while enrolled */}
      {deleteBlockInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b flex items-center gap-3">
              <AlertTriangle className="text-amber-500 flex-shrink-0" size={28} />
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Cannot Delete Customer</h3>
                <p className="text-sm text-gray-500">{deleteBlockInfo.customer?.name}</p>
              </div>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-gray-700">
                This customer is enrolled in <span className="font-semibold text-amber-700">
                  {deleteBlockInfo.enrollments?.length} active Gold Scheme{deleteBlockInfo.enrollments?.length > 1 ? 's' : ''}
                </span>. Complete or cancel all enrollments before deleting.
              </p>
              <div className="space-y-2">
                {deleteBlockInfo.enrollments?.map(e => (
                  <div key={e.id} className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-amber-800">{e.scheme_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {e.status}
                      </span>
                    </div>
                    <div className="text-gray-600 mt-1 flex gap-4">
                      <span>Paid: ₹{parseFloat(e.paid_amount || 0).toLocaleString('en-IN')}</span>
                      <span>Matures: {new Date(e.maturity_date).toLocaleDateString('en-IN')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button onClick={() => setDeleteBlockInfo(null)}
                className="px-5 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900">
                OK, Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog (from detail panel) */}
      {deleteConfirmCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
            <AlertTriangle className="text-red-500 mx-auto mb-3" size={36} />
            <h3 className="font-bold text-gray-900 text-lg mb-1">Delete Customer?</h3>
            <p className="text-gray-500 text-sm mb-5">
              This will permanently remove <span className="font-semibold text-gray-700">{deleteConfirmCustomer.name}</span>. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setDeleteConfirmCustomer(null)}
                className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handleDeleteFromPanel(deleteConfirmCustomer)}
                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Panel */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Customer Details</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => handleEditCustomer(selectedCustomer, e)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100"
                  >
                    <Edit2 size={15} /> Edit
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => setDeleteConfirmCustomer(selectedCustomer)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100"
                    >
                      <Trash2 size={15} /> Delete
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedCustomer(null)}
                    className="text-gray-400 hover:text-gray-600 ml-1"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="text-base font-medium text-gray-900">{selectedCustomer.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Customer Code</p>
                    <p className="text-base font-medium text-blue-600">{selectedCustomer.customer_code}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="text-base font-medium text-gray-900">{selectedCustomer.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-base font-medium text-gray-900">{selectedCustomer.email || 'N/A'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">City</p>
                    <p className="text-base font-medium text-gray-900">{selectedCustomer.city || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">State</p>
                    <p className="text-base font-medium text-gray-900">{selectedCustomer.state || 'N/A'}</p>
                  </div>
                </div>

                {selectedCustomer.address && (
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="text-base font-medium text-gray-900">{selectedCustomer.address}</p>
                  </div>
                )}

                {/* Statistics */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Transaction Statistics</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Total Estimates</p>
                      <p className="text-2xl font-bold text-blue-600">{selectedCustomer.total_estimates || 0}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">INR Value</p>
                      <p className="text-xl font-bold text-green-600">
                        ₹{parseFloat(selectedCustomer.total_inr || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">USD Value</p>
                      <p className="text-xl font-bold text-purple-600">
                        ${parseFloat(selectedCustomer.total_usd || 0).toLocaleString('en-US')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-gray-600">Created On</p>
                  <p className="text-base font-medium text-gray-900">
                    {new Date(selectedCustomer.created_at).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Create / Edit Modal */}
      <CustomerFormModal
        isOpen={showCustomerModal}
        onClose={() => { setShowCustomerModal(false); setEditingCustomer(null); }}
        onCustomerCreated={handleCustomerCreated}
        customer={editingCustomer}
        onCustomerDeleted={handleCustomerDeleted}
      />
    </div>
  );
};

export default CustomerList;
